import { inject, Injectable } from '@angular/core';
import { ADYEN_RESUME } from '@constants/adyen-resume.constants';
import { AppointmentBookingModel } from '@models/appointment-booking.model';
import {
  AdyenResume,
  AdyenResumeBookingSnapshot,
  AdyenResumeRecord,
  AdyenReturnParams,
} from '@models/adyen-resume.model';
import { SelectedBookingDataModel } from '@models/selected-booking-data.model';
import { WidgetService } from '@services/feature/widget.service';
import { LocalstorageService } from '@services/utils/localstorage.service';
import { LoggerService } from '@services/utils/logger.service';

/**
 * The app-level half of an Adyen payment that left the page: a Drop-in redirect, or the hosted page
 * opened in the same tab. `cw-adyen` writes the record right before the page navigates; the booking
 * component reads it on the way back - before anything else touches the booking - restores the
 * summary and hands the resume to the next `cw-adyen` that mounts.
 *
 * Only a return that PROVES itself (a `redirectResult`, or the hosted nonce of the stored record) is
 * resumed. A bare `redirectResult` with no record never books anything.
 */
@Injectable({
  providedIn: 'root'
})
export class AdyenResumeService {
  private localStorageService = inject(LocalstorageService);
  private widgetService = inject(WidgetService);

  /** Set by the booking component on a verified return, taken once by `cw-adyen`. */
  private pendingResume: AdyenResume | null = null;

  /**
   * The last `detectReturn()` saw a hosted return this page has no record of - a payment tab the
   * return page sent here because its opener was lost. That tab must not offer a fresh booking.
   */
  orphanHostedReturn = false;

  private get storageKey(): string {
    return `${ADYEN_RESUME.STORAGE_KEY_PREFIX}${this.widgetService.partnerUUID ?? 'unknown'}_${this.widgetService.widgetUUID ?? 'unknown'}`;
  }

  /**
   * Writes the record and reads it back. False when the browser would not keep it (storage full,
   * blocked, private mode): the caller must then NOT navigate away, or the payment comes back to a
   * page that cannot finish it.
   *
   * The WHOLE value is compared, not the session id: the storage wrapper swallows a quota error, and
   * an earlier, leaner record of the same session (the one beforeSubmit parks) would otherwise read
   * back as this write having succeeded.
   */
  save(record: AdyenResumeRecord): boolean {
    try {
      const serialized = JSON.stringify(record);
      this.localStorageService.setItem(this.storageKey, serialized);

      return this.readRawString() === serialized;
    } catch (error) {
      LoggerService.error(error);

      return false;
    }
  }

  /** The stored record, or null when there is none, it is expired, or it belongs to another widget. */
  read(): AdyenResumeRecord | null {
    const record = this.readRaw();
    if (!record) {
      return null;
    }
    const valid = record.v === ADYEN_RESUME.VERSION
      && !!record.sessionId
      && record.partnerUuid === this.widgetService.partnerUUID
      && record.widgetUuid === this.widgetService.widgetUUID
      && Number.isFinite(record.createdAt)
      && Date.now() - record.createdAt <= ADYEN_RESUME.TTL_MS;
    if (!valid) {
      this.clear();

      return null;
    }

    return record;
  }

  clear(): void {
    try {
      this.localStorageService.removeItem(this.storageKey);
    } catch (error) {
      LoggerService.error(error);
    }
  }

  /**
   * Drops the record of an attempt that was abandoned (the payment pane went away) - never one already
   * sent to `/book`, which must still say "already booked" on the next load.
   */
  discardUnsubmitted(sessionId: string | undefined): void {
    const record = sessionId ? this.readRaw() : null;
    if (record?.sessionId === sessionId && !record.submittedAt) {
      this.clear();
    }
  }

  /** Drops the record only while it still describes this session - a newer attempt may have replaced it. */
  clearIfOwnedBy(sessionId: string | undefined): void {
    if (sessionId && this.readRaw()?.sessionId === sessionId) {
      this.clear();
    }
  }

  /**
   * `/book` is being sent with this session. The record then keeps the payload and the full booking:
   * a page that dies before the answer replays exactly this request on its next load.
   */
  markSubmitted(
    sessionId: string | undefined,
    bookingItems: AppointmentBookingModel[],
    bookingData: SelectedBookingDataModel,
  ): void {
    const record = sessionId ? this.read() : null;
    if (record?.sessionId === sessionId) {
      this.save({
        ...record,
        booking: this.snapshot(bookingData),
        submittedBooking: bookingItems,
        submittedAt: Date.now(),
      });
    }
  }

  /**
   * The shopper's booking in the shape the record keeps. Without `includePersonalData` the shopper's
   * details and answers are left out - enough to show "already booked" for the slot, nothing a later
   * visitor of a shared device could read.
   */
  snapshot(bookingData: SelectedBookingDataModel, includePersonalData = true): AdyenResumeBookingSnapshot {
    return {
      cartItems: bookingData?.cartItems,
      selectedUser: includePersonalData ? bookingData?.selectedUser : undefined,
      selectedCustomFieldValues: includePersonalData ? bookingData?.selectedCustomFieldValues : undefined,
      selectedCoupon: bookingData?.selectedCoupon,
      totalBookingAmount: bookingData?.totalBookingAmount,
      totalDiscount: bookingData?.totalDiscount,
      subTotal: bookingData?.subTotal,
      timezone: bookingData?.timezone,
      currentSelectedAppointmentState: bookingData?.currentSelectedAppointmentState,
    };
  }

  /**
   * Called on every load. Reads the Adyen return parameters of the current page, removes them from
   * the address bar (before any new return URL is built from it, and so Back cannot replay them), and
   * matches them to the stored record. Reading the record also drops an expired one, whatever else
   * this load is.
   *
   * A load WITHOUT parameters still resumes a record that proves a payment in flight: one already
   * sent to `/book` (its request is replayed), a hosted payment whose page itself went to Adyen (its
   * nonce and token poll the result), or a Drop-in return whose `redirectResult` was copied into the
   * record before the address bar was cleaned. That is the reload, or the tab a phone discarded and
   * reloaded. Null when this load resumes nothing.
   *
   * A record that cannot finish a booking - no shopper details, no payload to replay - is dropped,
   * never restored: a summary with no customer can only take a payment it cannot book.
   */
  detectReturn(): AdyenResume | null {
    this.orphanHostedReturn = false;
    const params = this.readReturnParams();
    const record = this.read();

    if (!params.redirectResult && !params.hostedNonce) {
      return record ? this.resumeFromRecord(record) : null;
    }

    const isDropinReturn = record?.kind === ADYEN_RESUME.KIND.DROPIN
      && !!params.redirectResult
      && (!params.sessionId || params.sessionId === record.sessionId);
    const isHostedReturn = record?.kind === ADYEN_RESUME.KIND.HOSTED
      && !!params.hostedNonce
      && params.hostedNonce === record.nonce;

    // Kept BEFORE the address bar loses it: a reload from here on has nothing else to finish with.
    const matched = isDropinReturn
      ? { ...record, redirectResult: params.redirectResult }
      : record;
    if (isDropinReturn) {
      this.save(matched);
    }
    this.cleanReturnParams();

    if (!matched || (!isDropinReturn && !isHostedReturn)) {
      // A return with no record of ours - a stale link, another tab's payment, a forged parameter.
      // A hosted one is the payment tab itself, sent back here without an opener.
      this.orphanHostedReturn = !!params.hostedNonce;
      return null;
    }
    if (!this.isResumable(matched)) {
      this.clear();
      return null;
    }

    return {
      record: matched,
      redirectResult: isDropinReturn ? params.redirectResult : null,
      bookingSubmitted: !!matched.submittedAt,
    };
  }

  /** A record found on a load with no return parameters, when it proves a payment this page can finish. */
  private resumeFromRecord(record: AdyenResumeRecord): AdyenResume | null {
    if (record.submittedAt) {
      if (!this.isResumable(record)) {
        this.clear();
        return null;
      }
      return { record, redirectResult: record.redirectResult ?? null, bookingSubmitted: true };
    }

    // A popup payment is NOT resumed by a plain reload: the popup reaches this page through its
    // opener or through `return_to`, and a popup the shopper closed unpaid would otherwise pin every
    // load to this summary until the session expires. Only a page that itself went to Adyen is.
    const proven = (record.kind === ADYEN_RESUME.KIND.HOSTED && !!record.leftPage && !!record.nonce && !!record.token)
      || (record.kind === ADYEN_RESUME.KIND.DROPIN && !!record.redirectResult);
    if (!proven) {
      // A Drop-in payment that never came back from its redirect, or a popup payment: nothing to
      // finish here. The record stays for the popup's own return; a new attempt overwrites it.
      return null;
    }
    if (!this.isResumable(record)) {
      this.clear();
      return null;
    }

    return {
      record,
      redirectResult: record.kind === ADYEN_RESUME.KIND.DROPIN ? record.redirectResult ?? null : null,
      bookingSubmitted: false,
    };
  }

  /**
   * Whether the record carries what finishing it needs: the payload to replay once `/book` was sent,
   * otherwise the shopper's details the booking is made for.
   */
  private isResumable(record: AdyenResumeRecord): boolean {
    if (record.submittedAt) {
      return Array.isArray(record.submittedBooking) && record.submittedBooking.length > 0;
    }

    return !!record.booking?.selectedUser;
  }

  setPendingResume(resume: AdyenResume | null): void {
    this.pendingResume = resume;
  }

  peekPendingResume(): AdyenResume | null {
    return this.pendingResume;
  }

  /** The resume waiting for a payment pane, handed out exactly once. */
  takePendingResume(): AdyenResume | null {
    const resume = this.pendingResume;
    this.pendingResume = null;

    return resume;
  }

  hasPendingResume(): boolean {
    return !!this.pendingResume;
  }

  /**
   * The page URL with every Adyen return parameter and the fragment removed - the only URL a new
   * `return_url` / `return_to` may be built from, so a refused attempt's `redirectResult` is never
   * carried into the next session.
   */
  cleanPageUrl(href: string): string {
    try {
      const url = new URL(href);
      Object.values(ADYEN_RESUME.RETURN_PARAMS).forEach(param => url.searchParams.delete(param));
      url.hash = '';
      url.username = '';
      url.password = '';

      return url.toString();
    } catch {
      return href;
    }
  }

  readReturnParams(): AdyenReturnParams {
    try {
      const search = new URLSearchParams(window.location.search);

      return {
        redirectResult: search.get(ADYEN_RESUME.RETURN_PARAMS.REDIRECT_RESULT),
        sessionId: search.get(ADYEN_RESUME.RETURN_PARAMS.SESSION_ID),
        hostedNonce: search.get(ADYEN_RESUME.RETURN_PARAMS.HOSTED_NONCE),
      };
    } catch (error) {
      LoggerService.error(error);

      return { redirectResult: null, sessionId: null, hostedNonce: null };
    }
  }

  cleanReturnParams(): void {
    try {
      const cleaned = this.cleanPageUrl(window.location.href);
      // Keeps the fragment: only the query is ours to clean in the address bar, and a host SPA may route on it.
      const url = new URL(cleaned);
      url.hash = window.location.hash;
      // Preserve history.state - in WC mode it is the HOST SPA's routing state.
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      LoggerService.error(error);
    }
  }

  private readRaw(): AdyenResumeRecord | null {
    try {
      const raw = this.readRawString();

      return raw ? JSON.parse(raw) as AdyenResumeRecord : null;
    } catch (error) {
      LoggerService.error(error);

      return null;
    }
  }

  private readRawString(): string | null {
    try {
      return this.localStorageService.getItem(this.storageKey) || null;
    } catch (error) {
      LoggerService.error(error);

      return null;
    }
  }
}
